import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../../utils/seo';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const openTallyForm = () => {
    if (typeof window !== 'undefined' && (window as any).Tally) {
      (window as any).Tally.openPopup('gDdqKK', {
        layout: 'modal',
        width: 700,
        autoClose: 3000,
      });
    }
  };

  // Handle scroll effect
  useState(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  });

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    '@type': 'EducationalOrganization',
    name: 'Community Iftar Project',
    description: 'Host a Community Iftar event in your school or community centre during Ramadan 2026. Building community cohesion through shared meals and understanding.',
    url: siteUrl,
    logo: `${siteUrl}/vite.svg`,
    sameAs: [],
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Bristol',
      addressCountry: 'GB',
    },
    offers: {
      '@type': 'Offer',
      name: 'Community Iftar Event Support',
      price: '0',
      priceCurrency: 'GBP',
      availability: 'https://schema.org/InStock',
      validFrom: '2025-01-01',
      priceValidUntil: '2026-01-15',
    },
  };

  const faqSchema = {
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What is a Community Iftar?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'A Community Iftar is an event where people from all backgrounds come together to share the iftar meal - the meal that breaks the fast at sunset during Ramadan. It\'s an opportunity to build community cohesion and understanding in our diverse society.'
        }
      },
      {
        '@type': 'Question',
        name: 'When is Ramadan 2026?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Ramadan 2026 is expected to take place during 18th February - 17th March 2026. The exact dates depend on the lunar calendar and moon sighting. Ramadan will therefore take place during Term 4 of the academic year.'
        }
      },
      {
        '@type': 'Question',
        name: 'What is the registration deadline?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'The registration deadline is Friday 16th January 2026. After registering, you\'ll attend an online introduction workshop during the week of 19th January 2026.'
        }
      },
      {
        '@type': 'Question',
        name: 'Is the financial grant guaranteed?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'We can award up to £250 per school, but please note we have limited funds and do our best to distribute evenly amongst the city of Bristol. Grant monitoring and reporting will be required.'
        }
      }
    ]
  };

  return (
    <>
      <SEO
        title="Community Iftar Project | Bringing Communities Together in Bristol"
        description="Host a Community Iftar event in your school or community centre during Ramadan 2026. Building community cohesion through shared meals and understanding."
        keywords="Community Iftar, Ramadan, Bristol, community events, interfaith, school events"
        canonical={`${siteUrl}/`}
        ogTitle="Community Iftar Project | Bringing Communities Together in Bristol"
        ogDescription="Host a Community Iftar event in your school or community centre during Ramadan 2026. Join our project today."
        ogType="website"
        ogUrl={`${siteUrl}/`}
        twitterTitle="Community Iftar Project | Bringing Communities Together in Bristol"
        twitterDescription="Host a Community Iftar event in your school or community centre during Ramadan 2026."
        schema={[schema, faqSchema]}
      />
      <div className="relative min-h-screen bg-[#0F1829] overflow-x-hidden">
        {/* Fixed Background Elements */}
        <div className="fixed inset-0 z-0 pointer-events-none">
          <div className="absolute inset-0 opacity-20">
            <img 
              src="https://www.rahmaniyyah.com/calligraphy-background-stroke.png"
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute bottom-[-15%] left-[-10%] w-[55%] h-[70%] bg-[#1a4d5c]/20 blur-[80px] opacity-20"></div>
            <div className="absolute top-[-10%] right-[-10%] w-[50%] h-[60%] bg-[#2d5a6e]/20 blur-[90px] opacity-15"></div>
          </div>
        </div>

        {/* Top Navigation */}
        <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`}>
          <nav className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex items-center justify-between h-16 sm:h-20">
              {/* Logo */}
              <button onClick={scrollToTop} className="flex-shrink-0 cursor-pointer">
                <img 
                  src={isScrolled ? "https://static.readdy.ai/image/63f023a6a974325a1bd72d45110abc6b/49f4a0d491d56ed3fdb8c130286fff03.png" : "https://static.readdy.ai/image/63f023a6a974325a1bd72d45110abc6b/3f3a797d244b54989e1375000264c171.png"}
                  alt="Community Iftar Project"
                  className="h-12 sm:h-14"
                />
              </button>

              {/* Navigation Links */}
              <div className="hidden md:flex items-center gap-1">
                <button 
                  onClick={() => scrollToSection('mission')}
                  className={`px-4 py-2 rounded-lg text-base font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${isScrolled ? 'text-[#0F1829] hover:bg-[#0F1829]/5' : 'text-white hover:bg-white/10'}`}
                >
                  Home
                </button>
                <button 
                  onClick={() => scrollToSection('background')}
                  className={`px-4 py-2 rounded-lg text-base font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${isScrolled ? 'text-[#0F1829] hover:bg-[#0F1829]/5' : 'text-white hover:bg-white/10'}`}
                >
                  About
                </button>
                <button 
                  onClick={() => scrollToSection('roadmap')}
                  className={`px-4 py-2 rounded-lg text-base font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${isScrolled ? 'text-[#0F1829] hover:bg-[#0F1829]/5' : 'text-white hover:bg-white/10'}`}
                >
                  How It Works
                </button>
                <button 
                  onClick={() => scrollToSection('get-involved')}
                  className={`px-4 py-2 rounded-lg text-base font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${isScrolled ? 'text-[#0F1829] hover:bg-[#0F1829]/5' : 'text-white hover:bg-white/10'}`}
                >
                  Support
                </button>
                <button 
                  onClick={openTallyForm}
                  className={`px-6 py-2.5 bg-[#ED8C44] hover:bg-[#ED8C44]/90 text-white rounded-lg text-sm font-medium transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer ${isScrolled ? 'text-white' : 'text-white'}`}
                >
                  Register
                </button>
              </div>

              {/* Mobile Menu Button */}
              <button 
                onClick={() => {
                  const mobileMenu = document.getElementById('mobile-menu');
                  if (mobileMenu) {
                    mobileMenu.classList.toggle('hidden');
                  }
                }}
                className={`md:hidden p-2 rounded-lg transition-colors cursor-pointer ${isScrolled ? 'text-[#0F1829] hover:bg-[#0F1829]/5' : 'text-white hover:bg-white/10'}`}
              >
                <i className="ri-menu-line text-2xl"></i>
              </button>
            </div>

            {/* Mobile Menu */}
            <div id="mobile-menu" className="hidden md:hidden pb-4">
              <div className={`flex flex-col gap-2 p-4 rounded-xl ${isScrolled ? 'bg-white/50' : 'bg-[#0F1829]/50'} backdrop-blur-lg`}>
                <button 
                  onClick={() => {
                    scrollToSection('mission');
                    document.getElementById('mobile-menu')?.classList.add('hidden');
                  }}
                  className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 text-left whitespace-nowrap cursor-pointer ${isScrolled ? 'text-[#0F1829] hover:bg-[#0F1829]/5' : 'text-white hover:bg-white/10'}`}
                >
                  Home
                </button>
                <button 
                  onClick={() => {
                    scrollToSection('background');
                    document.getElementById('mobile-menu')?.classList.add('hidden');
                  }}
                  className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 text-left whitespace-nowrap cursor-pointer ${isScrolled ? 'text-[#0F1829] hover:bg-[#0F1829]/5' : 'text-white hover:bg-white/10'}`}
                >
                  About
                </button>
                <button 
                  onClick={() => {
                    scrollToSection('roadmap');
                    document.getElementById('mobile-menu')?.classList.add('hidden');
                  }}
                  className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 text-left whitespace-nowrap cursor-pointer ${isScrolled ? 'text-[#0F1829] hover:bg-[#0F1829]/5' : 'text-white hover:bg-white/10'}`}
                >
                  How It Works
                </button>
                <button 
                  onClick={() => {
                    scrollToSection('get-involved');
                    document.getElementById('mobile-menu')?.classList.add('hidden');
                  }}
                  className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 text-left whitespace-nowrap cursor-pointer ${isScrolled ? 'text-[#0F1829] hover:bg-[#0F1829]/5' : 'text-white hover:bg-white/10'}`}
                >
                  Support
                </button>
                <button 
                  onClick={() => {
                    openTallyForm();
                    document.getElementById('mobile-menu')?.classList.add('hidden');
                  }}
                  className="px-6 py-2.5 bg-[#ED8C44] hover:bg-[#ED8C44]/90 text-white rounded-lg text-sm font-medium transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer"
                >
                  Register
                </button>
              </div>
            </div>
          </nav>
        </header>

        <div className="relative z-10">
          {/* Hero Section */}
          <section id="mission" className="relative min-h-[70vh] flex flex-col items-center justify-center px-4 sm:px-6 pt-24 pb-16 overflow-hidden">
            {/* Background Image */}
            <div className="absolute inset-0 z-0">
              <img 
                src="https://static.readdy.ai/image/63f023a6a974325a1bd72d45110abc6b/53cf8a3331afd1a9812cee4e728aad75.png"
                alt=""
                className="w-full h-full object-cover"
              />
            </div>

            <div className="relative z-10 w-full max-w-7xl mx-auto flex flex-col items-center text-center">
              <div className="mb-8">
                <div className="relative inline-flex items-center px-8 py-3.5 rounded-full overflow-hidden group shadow-2xl">
                  <div className="absolute inset-0 border border-[#ED8C44]/20 bg-[#ED8C44]/5 backdrop-blur-[40px] rounded-full opacity-75"></div>
                  <div className="relative flex items-center gap-3">
                    <span className="text-sm sm:text-base font-black tracking-[0.2em] uppercase flex items-center gap-3">
                      <span className="text-black">Ramadan 2026</span>
                      <span className="w-1 h-1 rounded-full bg-white/30"></span>
                      <span className="text-white">February - March</span>
                    </span>
                  </div>
                </div>
              </div>

              <h1 className="max-w-5xl mx-auto text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-white mb-6 leading-[0.95]">
                Bringing Communities Together In Bristol
              </h1>

              <p className="max-w-2xl mx-auto text-lg sm:text-xl text-slate-200 font-bold leading-relaxed mb-10 px-4">
                Host a Community Iftar event in your school, local community centre, organisation.
              </p>

              <div className="flex flex-col items-center gap-4 w-full max-w-sm">
                <button 
                  onClick={openTallyForm}
                  className="group relative w-full px-8 py-5 bg-[#ED8C44] hover:bg-[#ED8C44]/90 text-[#0F1829] font-bold rounded-2xl transition-all duration-300 shadow-[0_0_30px_rgba(237,140,68,0.2)] hover:shadow-[0_0_50px_rgba(237,140,68,0.4)] hover:-translate-y-1 overflow-hidden whitespace-nowrap cursor-pointer"
                >
                  <span className="relative z-10 flex items-center justify-center gap-2 text-lg">
                    Register Your Interest
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
                </button>
                <div className="flex items-center gap-2">
                  <span className="text-black text-base sm:text-lg font-bold uppercase tracking-wider">Register by Friday 16th January</span>
                </div>
              </div>
            </div>
          </section>

          {/* Background Section */}
          <section id="background" className="relative py-16 sm:py-20 md:py-24 px-4 sm:px-6 overflow-hidden bg-white">
            <div className="relative z-10 max-w-4xl mx-auto">
              <div className="text-center mb-12 sm:mb-16">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0F1829]/5 border border-[#0F1829]/10 backdrop-blur-md mb-4">
                  <span className="w-2 h-2 rounded-full bg-[#ED8C44] animate-pulse"></span>
                  <span className="text-[10px] font-bold text-[#ED8C44] uppercase tracking-widest">Our Story</span>
                </div>
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#0F1829] mb-4 tracking-tight">
                  About the <span className="bg-gradient-to-r from-[#6ba3b8] to-[#0F1829] bg-clip-text text-transparent">Project</span>
                </h2>
              </div>

              <div className="space-y-6">
                {/* Background Brief */}
                <div className="relative rounded-2xl overflow-hidden transition-all duration-300 group">
                  <div className="absolute inset-0 border border-[#0F1829]/10 bg-[#0F1829]/[0.02] rounded-2xl opacity-75 group-hover:opacity-100 group-hover:border-[#0F1829]/15 transition-opacity"></div>
                  <div className="relative p-6 sm:p-8">
                    <h3 className="text-xl sm:text-2xl font-bold text-[#0F1829] mb-4 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#ED8C44]/20 flex items-center justify-center shrink-0">
                        <i className="ri-history-line text-[#ED8C44] text-xl"></i>
                      </div>
                      Background
                    </h3>
                    <div className="space-y-4 text-slate-600 text-sm sm:text-base leading-relaxed">
                      <p>
                        The project came into inception in <strong className="text-[#0F1829]">2016</strong> after the global community experienced many tragedies. Since 2016, the project has been involved in supporting schools in Bristol to host Community Iftar events. In addition to directly supporting schools, we have inspired many schools to host their own versions of the event.
                      </p>
                      <p>
                        The iftar is a meal that fasting Muslims have when they open their fast at sunset (Maghrib time) during the month of Ramadan. The month of Ramadan is the 9th month of the Islamic Calendar (based on the lunar calendar), in which Muslims fast from before dawn to sunset. Ramadan is one of the sacred months of the Islamic calendar where Muslims from all backgrounds participate in fasting (sawm), which is the 4th pillar of Islam.
                      </p>
                    </div>
                  </div>
                </div>

                {/* The Why */}
                <div className="relative rounded-2xl overflow-hidden transition-all duration-300 group">
                  <div className="absolute inset-0 border border-[#0F1829]/10 bg-[#0F1829]/[0.02] rounded-2xl opacity-75 group-hover:opacity-100 group-hover:border-[#0F1829]/15 transition-opacity"></div>
                  <div className="relative p-6 sm:p-8">
                    <h3 className="text-xl sm:text-2xl font-bold text-[#0F1829] mb-4 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#6ba3b8]/20 flex items-center justify-center shrink-0">
                        <i className="ri-heart-line text-[#6ba3b8] text-xl"></i>
                      </div>
                      The Why?
                    </h3>
                    <div className="space-y-4 text-slate-600 text-sm sm:text-base leading-relaxed">
                      <p className="font-bold text-[#ED8C44]">
                        We can summarise this in one sentence: <strong className="text-[#0F1829]">bringing communities together</strong>, and we believe schools are right at the heart of the communities in our society; they are the ultimate community hubs.
                      </p>
                      <p>
                        The Community Iftar events are centred around the opening meal (iftar), inviting the community to break bread together and build community cohesion in our ever-multi-diverse society.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Roadmap Section */}
          <section id="roadmap" className="relative py-16 sm:py-20 md:py-24 px-4 sm:px-6 overflow-hidden bg-white">
            <div className="relative z-10 max-w-4xl mx-auto">
              <div className="text-center mb-12 sm:mb-16">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0F1829]/5 border border-[#0F1829]/10 backdrop-blur-md mb-4">
                  <span className="w-2 h-2 rounded-full bg-[#ED8C44] animate-pulse"></span>
                  <span className="text-[10px] font-bold text-[#ED8C44] uppercase tracking-widest">How It Works</span>
                </div>
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#0F1829] mb-4 tracking-tight">
                  How to Get <span className="bg-gradient-to-r from-[#6ba3b8] to-[#0F1829] bg-clip-text text-transparent">Involved?</span>
                </h2>
              </div>

              <div className="space-y-6 sm:space-y-8">
                {/* Step 1 */}
                <div className="relative rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 group">
                  <div className="absolute inset-0 border border-[#0F1829]/10 bg-[#0F1829]/[0.02] rounded-2xl opacity-75 group-hover:opacity-100 group-hover:border-[#0F1829]/15 transition-opacity"></div>
                  <div className="relative p-6 sm:p-8">
                    <div className="flex items-start gap-4 sm:gap-6">
                      <div className="shrink-0">
                        <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-[#ED8C44]/20 text-[#ED8C44] flex items-center justify-center group-hover:bg-[#ED8C44]/30 transition-colors">
                          <span className="text-2xl sm:text-3xl font-bold">1</span>
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl sm:text-2xl font-bold text-[#0F1829] mb-2">Register your interest</h3>
                        <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                          Want to host a Community Iftar? Complete the form by <strong className="text-[#ED8C44]">16 January 2026</strong>. We'll send you a planning pack to help you get started.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="relative rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 group">
                  <div className="absolute inset-0 border border-[#0F1829]/10 bg-[#0F1829]/[0.02] rounded-2xl opacity-75 group-hover:opacity-100 group-hover:border-[#0F1829]/15 transition-opacity"></div>
                  <div className="relative p-6 sm:p-8">
                    <div className="flex items-start gap-4 sm:gap-6">
                      <div className="shrink-0">
                        <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-[#ED8C44]/20 text-[#ED8C44] flex items-center justify-center group-hover:bg-[#ED8C44]/30 transition-colors">
                          <span className="text-2xl sm:text-3xl font-bold">2</span>
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl sm:text-2xl font-bold text-[#0F1829] mb-2">Attend an online introduction workshop</h3>
                        <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                          Join a live workshop during the week of <strong className="text-[#ED8C44]">19 January 2026</strong>. Multiple sessions will be available to suit your schedule.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="relative rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 group">
                  <div className="absolute inset-0 border border-[#0F1829]/10 bg-[#0F1829]/[0.02] rounded-2xl opacity-75 group-hover:opacity-100 group-hover:border-[#0F1829]/15 transition-opacity"></div>
                  <div className="relative p-6 sm:p-8">
                    <div className="flex items-start gap-4 sm:gap-6">
                      <div className="shrink-0">
                        <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-[#ED8C44]/20 text-[#ED8C44] flex items-center justify-center group-hover:bg-[#ED8C44]/30 transition-colors">
                          <span className="text-2xl sm:text-3xl font-bold">3</span>
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl sm:text-2xl font-bold text-[#0F1829] mb-2">Book a coordination meeting</h3>
                        <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                          You'll be assigned a dedicated coordinator who will support you in planning and delivering your Community Iftar from start to finish.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* How to Get Involved Section */}
          <section id="get-involved" className="relative py-16 sm:py-20 md:py-24 px-4 sm:px-6 overflow-hidden bg-white">
            <div className="relative z-10 max-w-4xl mx-auto">
              <div className="text-center mb-12 sm:mb-16">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0F1829]/5 border border-[#0F1829]/10 backdrop-blur-md mb-4">
                  <span className="w-2 h-2 rounded-full bg-[#ED8C44] animate-pulse"></span>
                  <span className="text-[10px] font-bold text-[#ED8C44] uppercase tracking-widest">Support Available</span>
                </div>
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#0F1829] mb-4 tracking-tight">
                  We Can <span className="bg-gradient-to-r from-[#6ba3b8] to-[#0F1829] bg-clip-text text-transparent">Support</span>
                </h2>
                <p className="text-slate-600 text-sm sm:text-base max-w-2xl mx-auto">
                  Host your own Community Iftar event with support from the Community Iftar Project Team
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
                {/* Planning & Organising Support */}
                <div className="relative rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 group">
                  <div className="absolute inset-0 border border-[#0F1829]/10 bg-[#0F1829]/[0.02] rounded-2xl opacity-75 group-hover:opacity-100 group-hover:border-[#0F1829]/15 transition-opacity"></div>
                  <div className="relative p-6 sm:p-8">
                    <div className="w-12 h-12 rounded-xl bg-[#6ba3b8]/20 text-[#6ba3b8] flex items-center justify-center mb-5 group-hover:bg-[#6ba3b8]/30 transition-colors">
                      <i className="ri-calendar-check-line text-2xl"></i>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-[#0F1829] mb-3">Planning &amp; Organising</h3>
                    <p className="text-slate-600 text-sm sm:text-base leading-relaxed mb-4">
                      We can support you with:
                    </p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-3 text-sm text-slate-600">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#ED8C44]/70 shrink-0 mt-2"></div>
                        <span>Event itinerary planning</span>
                      </li>
                      <li className="flex items-start gap-3 text-sm text-slate-600">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#ED8C44]/70 shrink-0 mt-2"></div>
                        <span>Booking external speakers</span>
                      </li>
                      <li className="flex items-start gap-3 text-sm text-slate-600">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#ED8C44]/70 shrink-0 mt-2"></div>
                        <span>Managing external catering</span>
                      </li>
                      <li className="flex items-start gap-3 text-sm text-slate-600">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#ED8C44]/70 shrink-0 mt-2"></div>
                        <span>And much more</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Grant Support */}
                <div className="relative rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 group">
                  <div className="absolute inset-0 border border-[#0F1829]/10 bg-[#0F1829]/[0.02] rounded-2xl opacity-75 group-hover:opacity-100 group-hover:border-[#0F1829]/15 transition-opacity"></div>
                  <div className="relative p-6 sm:p-8">
                    <div className="w-12 h-12 rounded-xl bg-[#ED8C44]/20 text-[#ED8C44] flex items-center justify-center mb-5 group-hover:bg-[#ED8C44]/30 transition-colors">
                      <i className="ri-hand-coin-line text-2xl"></i>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-[#0F1829] mb-3">Financial Grant</h3>
                    <p className="text-slate-600 text-sm sm:text-base leading-relaxed mb-4">
                      We can award up to <strong className="text-[#ED8C44]">£250.00</strong> grant per school.
                    </p>
                    <div className="space-y-3 p-4 rounded-xl bg-[#0F1829]/[0.02] border border-[#0F1829]/5">
                      <p className="text-slate-500 text-xs sm:text-sm leading-relaxed">
                        <strong className="text-[#0F1829]">Please note:</strong> We have limited funds and do our best to distribute evenly amongst the city of Bristol.
                      </p>
                      <p className="text-slate-500 text-xs sm:text-sm leading-relaxed">
                        Grant monitoring and reporting will be required.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-10 text-center">
                <button 
                  onClick={openTallyForm}
                  className="group relative inline-flex px-8 py-4 bg-[#ED8C44] hover:bg-[#ED8C44]/90 text-white font-bold rounded-xl transition-all duration-300 shadow-[0_0_30px_rgba(237,140,68,0.2)] hover:shadow-[0_0_50px_rgba(237,140,68,0.4)] hover:-translate-y-1 overflow-hidden whitespace-nowrap cursor-pointer"
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    Register Your Interest
                    <i className="ri-arrow-right-line text-lg"></i>
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
                </button>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section id="faq" className="relative py-16 sm:py-20 md:py-24 px-4 sm:px-6 overflow-hidden bg-white">
            <div className="relative z-10 max-w-4xl mx-auto">
              <div className="text-center mb-12 sm:mb-16">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0F1829]/5 border border-[#0F1829]/10 backdrop-blur-md mb-4">
                  <span className="w-2 h-2 rounded-full bg-[#ED8C44] animate-pulse"></span>
                  <span className="text-[10px] font-bold text-[#ED8C44] uppercase tracking-widest">Common Questions</span>
                </div>
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#0F1829] mb-4 tracking-tight">
                  Frequently Asked <span className="bg-gradient-to-r from-[#6ba3b8] to-[#0F1829] bg-clip-text text-transparent">Questions</span>
                </h2>
                <p className="text-slate-600 text-sm sm:text-base max-w-2xl mx-auto">
                  Find answers to common questions about hosting a Community Iftar event
                </p>
              </div>

              <div className="space-y-4">
                {/* FAQ Item 1 */}
                <div className="relative rounded-2xl overflow-hidden transition-all duration-300 group">
                  <div className="absolute inset-0 border border-[#0F1829]/10 bg-[#0F1829]/[0.02] rounded-2xl opacity-75 group-hover:opacity-100 group-hover:border-[#0F1829]/15 transition-opacity"></div>
                  <div className="relative">
                    <button
                      onClick={() => toggleFaq(0)}
                      className="w-full px-6 sm:px-8 py-5 sm:py-6 flex items-center justify-between gap-4 text-left cursor-pointer"
                    >
                      <h3 className="text-lg sm:text-xl font-bold text-[#0F1829]">
                        What is a Community Iftar?
                      </h3>
                      <div className={`w-8 h-8 rounded-lg bg-[#ED8C44]/20 flex items-center justify-center shrink-0 transition-transform duration-300 ${openFaqIndex === 0 ? 'rotate-180' : ''}`}>
                        <i className="ri-arrow-down-s-line text-[#ED8C44] text-xl"></i>
                      </div>
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ${openFaqIndex === 0 ? 'max-h-96' : 'max-h-0'}`}>
                      <div className="px-6 sm:px-8 pb-5 sm:pb-6">
                        <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                          A Community Iftar is an event where people from all backgrounds come together to share the iftar meal - the meal that breaks the fast at sunset during Ramadan. It's an opportunity to build community cohesion and understanding in our diverse society.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* FAQ Item 2 */}
                <div className="relative rounded-2xl overflow-hidden transition-all duration-300 group">
                  <div className="absolute inset-0 border border-[#0F1829]/10 bg-[#0F1829]/[0.02] rounded-2xl opacity-75 group-hover:opacity-100 group-hover:border-[#0F1829]/15 transition-opacity"></div>
                  <div className="relative">
                    <button
                      onClick={() => toggleFaq(1)}
                      className="w-full px-6 sm:px-8 py-5 sm:py-6 flex items-center justify-between gap-4 text-left cursor-pointer"
                    >
                      <h3 className="text-lg sm:text-xl font-bold text-[#0F1829]">
                        When is Ramadan 2026?
                      </h3>
                      <div className={`w-8 h-8 rounded-lg bg-[#ED8C44]/20 flex items-center justify-center shrink-0 transition-transform duration-300 ${openFaqIndex === 1 ? 'rotate-180' : ''}`}>
                        <i className="ri-arrow-down-s-line text-[#ED8C44] text-xl"></i>
                      </div>
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ${openFaqIndex === 1 ? 'max-h-96' : 'max-h-0'}`}>
                      <div className="px-6 sm:px-8 pb-5 sm:pb-6">
                        <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                          Ramadan 2026 is expected to take place during 18th February - 17th March 2026. The exact dates depend on the lunar calendar and moon sighting. Ramadan will therefore take place during Term 4 of the academic year.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* FAQ Item 3 */}
                <div className="relative rounded-2xl overflow-hidden transition-all duration-300 group">
                  <div className="absolute inset-0 border border-[#0F1829]/10 bg-[#0F1829]/[0.02] rounded-2xl opacity-75 group-hover:opacity-100 group-hover:border-[#0F1829]/15 transition-opacity"></div>
                  <div className="relative">
                    <button
                      onClick={() => toggleFaq(2)}
                      className="w-full px-6 sm:px-8 py-5 sm:py-6 flex items-center justify-between gap-4 text-left cursor-pointer"
                    >
                      <h3 className="text-lg sm:text-xl font-bold text-[#0F1829]">
                        What is Fasting and Ramadan?
                      </h3>
                      <div className={`w-8 h-8 rounded-lg bg-[#ED8C44]/20 flex items-center justify-center shrink-0 transition-transform duration-300 ${openFaqIndex === 2 ? 'rotate-180' : ''}`}>
                        <i className="ri-arrow-down-s-line text-[#ED8C44] text-xl"></i>
                      </div>
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ${openFaqIndex === 2 ? 'max-h-96' : 'max-h-0'}`}>
                      <div className="px-6 sm:px-8 pb-5 sm:pb-6">
                        <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                          Ramadan is the 9th month of the Islamic lunar calendar, during which Muslims fast from dawn to sunset. Fasting (Sawm) is the 4th pillar of Islam and involves abstaining from food, drink, and other physical needs during daylight hours. It's a time of spiritual reflection, increased devotion, worship, and community. The fast is broken each evening with the iftar meal at sunset.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* FAQ Item 4 */}
                <div className="relative rounded-2xl overflow-hidden transition-all duration-300 group">
                  <div className="absolute inset-0 border border-[#0F1829]/10 bg-[#0F1829]/[0.02] rounded-2xl opacity-75 group-hover:opacity-100 group-hover:border-[#0F1829]/15 transition-opacity"></div>
                  <div className="relative">
                    <button
                      onClick={() => toggleFaq(3)}
                      className="w-full px-6 sm:px-8 py-5 sm:py-6 flex items-center justify-between gap-4 text-left cursor-pointer"
                    >
                      <h3 className="text-lg sm:text-xl font-bold text-[#0F1829]">
                        How can I get involved?
                      </h3>
                      <div className={`w-8 h-8 rounded-lg bg-[#ED8C44]/20 flex items-center justify-center shrink-0 transition-transform duration-300 ${openFaqIndex === 3 ? 'rotate-180' : ''}`}>
                        <i className="ri-arrow-down-s-line text-[#ED8C44] text-xl"></i>
                      </div>
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ${openFaqIndex === 3 ? 'max-h-96' : 'max-h-0'}`}>
                      <div className="px-6 sm:px-8 pb-5 sm:pb-6">
                        <p className="text-[#0F1829]/70 leading-relaxed">
                          You can get involved by registering to host a Community Iftar event at your school. Simply fill out the registration form, and our team will provide you with all the resources and support you need to make your event a success.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Footer */}
          <footer className="relative py-8 sm:py-12 px-4 sm:px-6 bg-[#62C5C0]">
            <div className="max-w-6xl mx-auto">
              <div className="relative rounded-2xl overflow-hidden shadow-lg">
                <div className="absolute inset-0 border border-white/10 bg-white/[0.02] rounded-2xl opacity-75"></div>
                <div className="relative z-10 px-6 sm:px-10 py-8 sm:py-10">
                  <div className="flex flex-col items-center gap-6">
                    <div className="w-full flex flex-col sm:flex-row items-center justify-between gap-6">
                      <div className="flex items-center">
                        <img 
                          src="https://static.readdy.ai/image/63f023a6a974325a1bd72d45110abc6b/3f3a797d244b54989e1375000264c171.png"
                          alt="Al-Madrasatu Ar-Raḥmāniyyah"
                          className="opacity-80 hover:opacity-100 transition-opacity duration-300 h-16"
                        />
                      </div>
                      <div className="flex items-center gap-4 text-base">
                        <a 
                          href="mailto:ismaeel@ummahtrust.org.uk"
                          className="text-white hover:text-white/80 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                        >
                          Need help?
                        </a>
                        <span className="text-white/20">|</span>
                        <Link to="/privacy" className="text-white hover:text-white/80 transition-colors duration-200 whitespace-nowrap">
                          Privacy Policy
                        </Link>
                      </div>
                    </div>
                    <div className="w-full h-px bg-white/10"></div>
                    <div className="w-full flex flex-col sm:flex-row items-center justify-between gap-2 text-center sm:text-left">
                      <p className="text-white text-sm font-bold">
                        Project of Bristol Muslim Cultural Society (BMCS) and Ummah Community Trust (UCT)
                      </p>
                      <p className="text-white text-xs">
                        Powered by Proud Bristolians
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </footer>

          {/* Scroll to Top Button */}
          <button
            onClick={scrollToTop}
            className={`fixed bottom-8 right-8 z-50 w-12 h-12 rounded-full bg-[#ED8C44] hover:bg-[#ED8C44]/90 text-white shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center cursor-pointer ${
              showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
            }`}
            aria-label="Scroll to top"
          >
            <i className="ri-arrow-up-line text-xl"></i>
          </button>
        </div>
      </div>
    </>
  );
}
